import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { CloudUpload, Delete, InsertDriveFile } from '@mui/icons-material';
import { apiServices } from '../services';

const ReferenceStatusForm = ({ 
  masterid, 
  refstatusFinal,
  onSubmit, 
  onCancel,
  isSubmitting = false,
  verifiedEmail = ''
}) => {
  const [formData, setFormData] = useState({
    email: verifiedEmail,
    refstatus_feedback: '',
    refstatus_photos: 'no'
  });
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // Clear files if switching to 'no'
    if (field === 'refstatus_photos' && event.target.value === 'no') {
      setSelectedFiles([]);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFilesAsZip = async () => {
    if (selectedFiles.length === 0) return null;
    
    try {
      // Zip files and upload to S3, returns URL
      const zipUrl = await apiServices.uploadFilesAsZip(selectedFiles, masterid, 'refstatus');
      return zipUrl;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.refstatus_feedback) {
      newErrors.refstatus_feedback = 'Feedback is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      // Upload files as zip if any
      let photosLink = null;
      if (formData.refstatus_photos === 'yes' && selectedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          photosLink = await uploadFilesAsZip();
        } catch (error) {
          setErrors(prev => ({ ...prev, upload: error.message || 'Failed to upload files. Please try again.' }));
          setUploadingFiles(false);
          return;
        }
        setUploadingFiles(false);
      }

      const submissionData = {
        masterid: masterid,
        email: formData.email,
        refstatus_feedback_date: new Date().toISOString(),
        refstatus_final: refstatusFinal,
        refstatus_feedback: formData.refstatus_feedback,
        refstatus_photos: formData.refstatus_photos,
        refstatus_photos_link: photosLink,
        refstatus_feedback_evaluator: null,
        refstatus_feedback_evaluationdate: null
      };
      console.log('Reference Status Feedback Submission:', submissionData);
      onSubmit(submissionData);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" color="primary">
        Reference Status Feedback
      </Typography>

      {/* Auto-populated fields display */}
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Master ID:</strong> {masterid || 'N/A'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <strong>Current Reference Status:</strong> {refstatusFinal || 'Unknown'}
        </Typography>
      </Box>

      {/* Email field */}
      <TextField
        label="Email"
        type="email"
        required
        fullWidth
        value={formData.email}
        onChange={handleChange('email')}
        error={!!errors.email}
        helperText={verifiedEmail ? 'Verified email address' : errors.email}
        placeholder="your.email@example.com"
        disabled={!!verifiedEmail}
        InputProps={{
          readOnly: !!verifiedEmail
        }}
        sx={verifiedEmail ? { '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' } } : {}}
      />

      {/* Reference Status Feedback */}
      <TextField
        label="Reference Status Feedback"
        required
        fullWidth
        multiline
        rows={4}
        value={formData.refstatus_feedback}
        onChange={handleChange('refstatus_feedback')}
        error={!!errors.refstatus_feedback}
        helperText={errors.refstatus_feedback || "What information do you have about the reference status of this site? E.g., are you aware of any human activities or disturbances that could affect this site?"}
        placeholder="Please describe any information you have about the reference status..."
      />

      {/* Photos/Data question */}
      <FormControl component="fieldset" required>
        <FormLabel component="legend">
          Do you have photos or data you'd like to share about the reference status of this site?
        </FormLabel>
        <RadioGroup
          row
          value={formData.refstatus_photos}
          onChange={handleChange('refstatus_photos')}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>

      {formData.refstatus_photos === 'yes' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            Please upload your photos or data files below.
          </Alert>
          
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept=".jpg,.jpeg,.png,.xls,.xlsx,.csv"
            multiple
          />
          
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting || uploadingFiles}
          >
            Select Files
          </Button>
          
          {selectedFiles.length > 0 && (
            <List dense>
              {selectedFiles.map((file, index) => (
                <ListItem key={index} sx={{ bgcolor: 'grey.50', mb: 0.5, borderRadius: 1 }}>
                  <InsertDriveFile sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText 
                    primary={file.name} 
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      onClick={() => handleRemoveFile(index)}
                      disabled={isSubmitting || uploadingFiles}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {errors.upload && (
            <Alert severity="error">{errors.upload}</Alert>
          )}
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting || uploadingFiles}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting || uploadingFiles}
          startIcon={(isSubmitting || uploadingFiles) ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {uploadingFiles ? 'Uploading...' : isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </Box>
    </Box>
  );
};

export default ReferenceStatusForm;
